/* eslint-disable @typescript-eslint/no-explicit-any */
import { refreshUserToken } from '@/entities/user/thunks/refreshUserToken'
import { store } from '@/shared/store'

import { isTokenExpired } from './JWTTokenHelpers'

class TokenRefreshManager {
  private isRefreshing = false

  private refreshPromise: Promise<any> | null = null

  private failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (reason?: any) => void
  }> = []

  private processQueue(error: any = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve()
      }
    })

    this.failedQueue = []
  }

  async refreshToken(): Promise<void> {
    const state = store.getState()
    const { refreshToken, isRefreshing } = state.session

    if (!refreshToken) {
      throw new Error('No refresh token available')
    }

    if (this.isRefreshing) {
      // If already refreshing, wait for the existing refresh to complete
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject })
      })
    }

    if (isRefreshing) {
      // Redux is already handling refresh, wait for it
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject })
      })
    }

    this.isRefreshing = true

    try {
      this.refreshPromise = store.dispatch(refreshUserToken()).unwrap()
      await this.refreshPromise
      this.processQueue()
    } catch (error) {
      this.processQueue(error)
      throw error
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  async ensureValidToken(): Promise<void> {
    const state = store.getState()
    const { accessToken, refreshToken } = state.session

    if (!accessToken || isTokenExpired(accessToken)) {
      if (refreshToken && !isTokenExpired(refreshToken)) {
        await this.refreshToken()
      } else {
        throw new Error('No valid tokens available')
      }
    }
  }

  isCurrentlyRefreshing(): boolean {
    return this.isRefreshing || store.getState().session.isRefreshing
  }
}

export const tokenRefreshManager = new TokenRefreshManager()